import React, { useState } from 'react';
import { Upload, Button, Modal, message, Spin } from 'antd';
import { UploadOutlined, FileTextOutlined, CloseOutlined } from '@ant-design/icons';
import type { UploadFile, UploadProps } from 'antd';

interface DocumentUploadProps {
  onFileUploaded: (s3Url: string, fileName: string) => void;
  onUploadStart?: () => void;
  onUploadComplete?: () => void;
}

const DocumentUpload: React.FC<DocumentUploadProps> = ({
  onFileUploaded,
  onUploadStart,
  onUploadComplete,
}) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  const acceptedTypes = '.pdf,.png,.jpg,.jpeg,.tiff,.bmp';
  const maxSize = 5 * 1024 * 1024; // 5MB

  const handleUpload = async (file: File) => {
    setUploading(true);
    onUploadStart?.();

    try {
      const formData = new FormData();
      formData.append('file', file);

      console.log('Uploading file:', file.name);
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/files/upload`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });

      if (!response.ok) {
        let errorMessage = 'Upload failed';
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorMessage;
        } catch {
          // ignore JSON parse error and use default message
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();

      message.success(`File uploaded successfully: ${file.name}`);
      onFileUploaded(result.s3_url, file.name);
      setIsModalVisible(false);
      setFileList([]);
    } catch (error: any) {
      console.error('Upload error:', error);
      message.error(`Failed to upload ${file.name}: ${error?.message ?? 'Unknown error'}`);
    } finally {
      setUploading(false);
      onUploadComplete?.();
    }
  };

  const uploadProps: UploadProps = {
    accept: acceptedTypes,
    multiple: false,
    fileList,
    beforeUpload: (file) => {
      // Validate size
      if (file.size > maxSize) {
        message.error('File size must be less than 5MB');
        // Prevent adding to the list
        // (so UI doesn't show an invalid file)
        return Upload.LIST_IGNORE;
      }

      // Validate extension
      const fileExtension = file.name.toLowerCase().split('.').pop();
      const allowedExtensions = ['pdf', 'png', 'jpg', 'jpeg', 'tiff', 'bmp'];
      if (!fileExtension || !allowedExtensions.includes(fileExtension)) {
        message.error('Only PDF and image files are supported');
        return Upload.LIST_IGNORE;
      }

      // Stop auto upload but keep the file in the list
      return false;
    },
    onChange: (info) => {
      // Keep AntD's UploadFile objects (these include originFileObj)
      setFileList(info.fileList.slice(-1)); // enforce single-file selection
    },
    onRemove: () => {
      setFileList([]);
    },
  };

  const handleModalOk = () => {
    if (fileList.length === 0) {
      message.warning('Please select a file first');
      return;
    }

    const raw = fileList[0].originFileObj as File | undefined;
    if (!raw) {
      message.error('Could not read the selected file. Please reselect and try again.');
      return;
    }
    handleUpload(raw);
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    setFileList([]);
  };

  const triggerFileSelect = () => {
    setIsModalVisible(true);
  };

  return (
    <>
      <Button
        type="text"
        icon={<UploadOutlined />}
        onClick={triggerFileSelect}
        className="document-upload-trigger"
        title="Upload document"
        size="small"
      />

      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <FileTextOutlined />
            Upload Document
          </div>
        }
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        confirmLoading={uploading}
        okText={uploading ? 'Uploading...' : 'Upload'}
        cancelText="Cancel"
        width={500}
        destroyOnClose
      >
        <div className="document-upload-content">
          <p>Upload a document to analyze with the AI agent.</p>

          <div className="upload-info">
            <h4>Supported formats:</h4>
            <ul>
              <li>PDF documents (.pdf)</li>
              <li>Images (.png, .jpg, .jpeg, .tiff, .bmp)</li>
            </ul>
            <p>
              <strong>Maximum file size:</strong> 5MB
            </p>
          </div>

          <Upload.Dragger {...uploadProps} className="document-upload-dragger">
            <div className="upload-dragger-content">
              {uploading ? (
                <Spin size="large" />
              ) : (
                <>
                  <UploadOutlined style={{ fontSize: 48 }} />
                  <p className="upload-text">Click or drag file to this area to upload</p>
                </>
              )}
            </div>
          </Upload.Dragger>

          {fileList.length > 0 && !uploading && (
            <div className="selected-file-info">
              <h4>Selected file:</h4>
              <div className="file-item" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <FileTextOutlined />
                <span>{fileList[0].name}</span>
                <Button
                  type="text"
                  size="small"
                  icon={<CloseOutlined />}
                  onClick={() => setFileList([])}
                />
              </div>
            </div>
          )}
        </div>
      </Modal>
    </>
  );
};

export default DocumentUpload;
